import { memo, ReactElement, useEffect, useState } from 'react';
import { NotApplicableToggle } from 'components/inspection/NotApplicableToggle';
import {
  useAtomicPersist,
  useInspectionFieldDisabled,
  isInspectionFieldDisabled,
} from 'store/inspection';

interface SectionNotApplicableToggleProps {
  sectionId: string;
  /** From `computeSectionApplicabilityById`, computed once for the whole tree. */
  canSkip: boolean;
  isNotApplicable: boolean;
  onSkippedChange?: (sectionId: string, skipped: boolean) => void;
}

function SectionNotApplicableToggle({
  sectionId,
  canSkip,
  isNotApplicable,
  onSkippedChange,
}: SectionNotApplicableToggleProps): ReactElement | null {
  const { saveProtocolSectionAnswersApplicability } = useAtomicPersist();
  const resolveFieldDisabled = useInspectionFieldDisabled();
  const fieldDisabled = resolveFieldDisabled(false);
  const disabled = isInspectionFieldDisabled(fieldDisabled);

  const [optimisticSkipped, setOptimisticSkipped] = useState<boolean | null>(
    null
  );

  useEffect(() => {
    if (optimisticSkipped === null) {
      return;
    }

    if (optimisticSkipped === isNotApplicable) {
      setOptimisticSkipped(null);
    }
  }, [isNotApplicable, optimisticSkipped]);

  if (!canSkip) {
    return null;
  }

  const isSkipped = optimisticSkipped ?? isNotApplicable;

  return (
    <NotApplicableToggle
      isSkipped={isSkipped}
      disabled={disabled}
      stopPropagation
      onClick={() => {
        const nextSkipped = !isSkipped;
        setOptimisticSkipped(nextSkipped);
        onSkippedChange?.(sectionId, nextSkipped);
        void saveProtocolSectionAnswersApplicability(
          sectionId,
          !nextSkipped
        ).then((outcome) => {
          if (outcome === 'failed') {
            setOptimisticSkipped(null);
            onSkippedChange?.(sectionId, isNotApplicable);
          }
        });
      }}
    />
  );
}

export const MemoSectionNotApplicableToggle = memo(SectionNotApplicableToggle);
